import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const DAYS_IT = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

const MiniCalendar = ({ 
  datesWithEntries = [], 
  selectedDate, 
  onDateSelect,
  className = ""
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = selectedDate ? new Date(selectedDate) : new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  // Update current month when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      const d = new Date(selectedDate);
      setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [selectedDate]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Get the day of week (0 = Sunday, 1 = Monday, etc.)
    // Convert to Monday-first (0 = Monday, 6 = Sunday)
    let startDay = firstDay.getDay() - 1;
    if (startDay < 0) startDay = 6;
    
    const days = [];
    
    // Add empty cells for days before the first of the month
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newMonth = new Date(prev);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return newMonth;
    });
  };

  const formatDateKey = (date) => {
    if (!date) return "";
    return date.toISOString().split("T")[0];
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    if (!date || !selectedDate) return false;
    return formatDateKey(date) === selectedDate;
  };

  const hasEntry = (date) => {
    if (!date) return false;
    const dateKey = formatDateKey(date);
    return datesWithEntries.includes(dateKey);
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className={`soft-card p-4 ${className}`} data-testid="mini-calendar">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={() => navigateMonth(-1)}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Mese precedente"
        >
          <ChevronLeft className="w-4 h-4 text-gray-500" />
        </button>
        <span className="text-sm font-semibold text-gray-700">
          {MONTHS_IT[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button
          onClick={() => navigateMonth(1)}
          className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Mese successivo"
        >
          <ChevronRight className="w-4 h-4 text-gray-500" />
        </button>
      </div>

      {/* Days header */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAYS_IT.map(day => (
          <div key={day} className="text-center text-[10px] font-medium text-gray-400 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {days.map((date, idx) => {
          if (!date) {
            return <div key={`empty-${idx}`} className="aspect-square" />;
          }
          
          const dateKey = formatDateKey(date);
          const today = isToday(date);
          const selected = isSelected(date);
          const withEntry = hasEntry(date);
          
          return (
            <button
              key={dateKey}
              onClick={() => onDateSelect(dateKey)}
              className={`
                aspect-square rounded-lg text-xs font-medium transition-all relative
                flex items-center justify-center
                ${selected 
                  ? "bg-indigo-600 text-white shadow-md" 
                  : today
                    ? "bg-indigo-100 text-indigo-700"
                    : withEntry
                      ? "bg-green-50 text-gray-700 hover:bg-green-100"
                      : "text-gray-600 hover:bg-gray-100"
                }
              `}
              data-testid={`calendar-day-${dateKey}`}
            >
              {date.getDate()}
              {/* Entry indicator dot */}
              {withEntry && !selected && (
                <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-green-500" />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-4 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-[10px] text-gray-500">Con appunti</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-indigo-600" />
          <span className="text-[10px] text-gray-500">Selezionato</span>
        </div>
      </div>
    </div>
  );
};

export default MiniCalendar;
